import React, { useState, useRef } from 'react';
import { X, Upload } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PromotionForm = ({ 
  promotion, 
  onSave, 
  onCancel, 
  authHeaders 
}) => {
  const [form, setForm] = useState({
    title: promotion?.title || '',
    description: promotion?.description || '',
    image_url: promotion?.image_url || '',
    link: promotion?.link || '',
    active: promotion?.active ?? true,
    order: promotion?.order || 0
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    onSave({
      ...form,
      image_url: form.image_url || 'https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=1200'
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Type de fichier non autorisé. Utilisez JPG, PNG, GIF ou WEBP.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("L'image est trop lourde. Maximum 5 Mo.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = response.data.url;
      setForm(prev => ({ ...prev, image_url: imageUrl }));
    } catch (error) {
      alert("Erreur lors de l'upload de l'image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {promotion ? 'Modifier la promotion' : 'Ajouter une promotion'}
          </h2>
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="promo-form">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              required
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626]"
              placeholder="Ex: 2 pneus achetés = 2 offerts"
              data-testid="promo-title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626] resize-none"
              placeholder="Description de l'offre..."
              data-testid="promo-description"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Image de la promotion (optionnel)</label>
            <div className="flex gap-3 mb-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                data-testid="promo-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors disabled:opacity-50"
                data-testid="promo-upload-btn"
              >
                {uploading ? (
                  <><div className="spinner h-4 w-4"></div>Upload en cours...</>
                ) : (
                  <><Upload size={18} />Choisir une image</>
                )}
              </button>
              <span className="text-xs text-gray-500 self-center">ou</span>
            </div>
            <input
              type="url"
              value={form.image_url}
              onChange={(e) => setForm(prev => ({ ...prev, image_url: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626]"
              placeholder="Coller une URL d'image..."
              data-testid="promo-image"
            />
            {form.image_url && (
              <div className="mt-3 relative inline-block">
                <img src={form.image_url} alt="Preview" className="h-32 object-cover rounded border" />
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, image_url: '' }))}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lien (optionnel)</label>
            <input
              type="text"
              value={form.link}
              onChange={(e) => setForm(prev => ({ ...prev, link: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626]"
              placeholder="https://... ou /contact"
              data-testid="promo-link"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ordre d'affichage</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC2626]"
                data-testid="promo-order"
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="promo-active"
                checked={form.active}
                onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked }))}
                className="w-4 h-4 text-[#DC2626] rounded"
                data-testid="promo-active"
              />
              <label htmlFor="promo-active" className="text-sm font-medium text-gray-700">
                Promotion active
              </label>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 cta-button justify-center"
              data-testid="save-promo-btn"
            >
              {promotion ? 'Enregistrer' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PromotionForm;
